import { useEffect, useRef } from "react";
import { motion, useInView } from "framer-motion";
import { Award, Target, Heart, Lightbulb } from "lucide-react";

const aboutImage = "https://images.unsplash.com/photo-1761718209694-70031ee64f82?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjh8MHwxfHNlYXJjaHwxfHxhZXN0aGV0aWNpYW4lMjBwb3J0cmFpdHxlbnwwfHx8fDE3NzUzNjIwODN8MA&ixlib=rb-4.1.0&q=85";

const values = [
  {
    icon: Award,
    title: "Excellence",
    description: "We maintain the highest standards in aesthetic education, ensuring our graduates are industry leaders.",
  },
  {
    icon: Target,
    title: "Precision",
    description: "Scientific methodology meets artistic technique for results that exceed expectations.",
  },
  {
    icon: Heart,
    title: "Dedication",
    description: "Our commitment to student success drives everything we do, from curriculum to support.",
  },
  {
    icon: Lightbulb,
    title: "Innovation",
    description: "Staying ahead with the latest technologies and techniques in luxury aesthetics.",
  },
];

const AnimatedSection = ({ children, className = "" }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  return (
    <motion.div
      ref={ref}
      initial={{ opacity: 0, y: 50 }}
      animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 50 }}
      transition={{ duration: 0.8 }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

export const AboutPage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div data-testid="about-page" className="pt-20">
      {/* Hero Section */}
      <section className="py-24 md:py-32 bg-[#050505]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                About Us
              </span>
              <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-light text-white mt-4 mb-6">
                Elegance Meets<br />
                <span className="gold-gradient">Expertise</span>
              </h1>
              <p className="font-body text-lg text-[#A3A3A3] leading-relaxed mb-6">
                LuxuryBeauty.CA was founded with a singular vision: to elevate the 
                standards of aesthetic education by combining refined techniques with 
                the artistry of luxury beauty care.
              </p>
              <p className="font-body text-lg text-[#A3A3A3] leading-relaxed">
                Our curriculum bridges the gap between traditional techniques and 
                modern innovations, preparing our students to become leaders 
                in the industry.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              className="relative"
            >
              <div className="absolute inset-0 border border-[#C5A059] translate-x-4 translate-y-4" />
              <img
                src={aboutImage}
                alt="Aesthetician at work"
                className="relative z-10 w-full h-[500px] object-cover grayscale hover:grayscale-0 transition-all duration-700"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="mission-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="max-w-3xl mx-auto text-center">
              <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                Our Mission
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-4 mb-8">
                Empowering Through Education
              </h2>
              <div className="section-divider" />
              <p className="font-body text-xl text-[#A3A3A3] leading-relaxed mt-8">
                "We believe that exceptional aesthetics training should combine 
                refined techniques with hands-on expertise. Our mission is 
                to create industry professionals who don't just perform treatments, 
                but master the art behind every technique."
              </p>
            </div>
          </AnimatedSection>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-24 md:py-32 bg-[#050505]" data-testid="values-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <AnimatedSection>
            <div className="text-center mb-16">
              <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                What Drives Us
              </span>
              <h2 className="font-heading text-3xl sm:text-4xl lg:text-5xl font-light text-white mt-4">
                Our Core Values
              </h2>
              <div className="section-divider" />
            </div>
          </AnimatedSection>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <AnimatedSection key={value.title}>
                <div
                  className="bento-card p-8 text-center h-full"
                  data-testid={`value-${index}`}
                >
                  <div className="w-16 h-16 mx-auto mb-6 border border-[#C5A059] flex items-center justify-center">
                    <value.icon className="w-7 h-7 text-[#C5A059]" />
                  </div>
                  <h3 className="font-heading text-xl text-white mb-3">{value.title}</h3>
                  <p className="font-body text-sm text-[#A3A3A3] leading-relaxed">
                    {value.description}
                  </p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Approach Section */}
      <section className="py-24 md:py-32 bg-[#0A0A0A]" data-testid="approach-section">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <AnimatedSection>
              <div className="grid grid-cols-2 gap-4">
                <div className="bento-card p-8 text-center">
                  <span className="font-heading text-5xl text-[#C5A059]">10+</span>
                  <p className="font-body text-sm text-[#A3A3A3] mt-2">Years Experience</p>
                </div>
                <div className="bento-card p-8 text-center">
                  <span className="font-heading text-5xl text-[#C5A059]">500+</span>
                  <p className="font-body text-sm text-[#A3A3A3] mt-2">Graduates</p>
                </div>
                <div className="bento-card p-8 text-center">
                  <span className="font-heading text-5xl text-[#C5A059]">98%</span>
                  <p className="font-body text-sm text-[#A3A3A3] mt-2">Success Rate</p>
                </div>
                <div className="bento-card p-8 text-center">
                  <span className="font-heading text-5xl text-[#C5A059]">4</span>
                  <p className="font-body text-sm text-[#A3A3A3] mt-2">Specializations</p>
                </div>
              </div>
            </AnimatedSection>

            <AnimatedSection>
              <div>
                <span className="font-body text-xs font-semibold tracking-[0.3em] uppercase text-[#C5A059]">
                  Our Approach
                </span>
                <h2 className="font-heading text-3xl sm:text-4xl font-light text-white mt-4 mb-6">
                  Excellence Meets Artistic Precision
                </h2>
                <div className="space-y-6">
                  <p className="font-body text-[#A3A3A3] leading-relaxed">
                    Our approach is rooted in the understanding that great aesthetics 
                    requires both knowledge and technical skill. We don't 
                    just teach procedures—we explain the why behind every technique.
                  </p>
                  <p className="font-body text-[#A3A3A3] leading-relaxed">
                    From understanding skin and nail care to mastering advanced treatment 
                    protocols, our students graduate with comprehensive knowledge that 
                    sets them apart in the competitive aesthetics industry.
                  </p>
                  <p className="font-body text-[#A3A3A3] leading-relaxed">
                    Our state-of-the-art facility provides the perfect environment for 
                    learning, with access to the latest equipment and technologies used 
                    in luxury aesthetics practices worldwide.
                  </p>
                </div>
              </div>
            </AnimatedSection>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
